import React from 'react';
import { Zap, TrendingUp, Target, Calendar, AlertTriangle, ClipboardList, ArrowRight } from 'lucide-react';
import { SectionCard, SaveBar, StringListEditor, ObjectListEditor, useDraft } from './shared';

const normalize = (plan) => ({
  quick_wins: plan?.quick_wins || [],
  strategic_priorities: plan?.strategic_priorities || [],
  action_plan_90day: plan?.action_plan_90day || [],
  roadmap_12month: plan?.roadmap_12month || [],
});

const ImprovementPlanTab = ({ sections, onSave, saving, generatedReview, onGoTo }) => {
  const initial = normalize(sections?.improvement_plan);
  const [draft, setDraft, dirty] = useDraft(initial);
  const set = (key, val) => setDraft((d) => ({ ...d, [key]: val }));

  const autoPlan = generatedReview?.plan_90day || [];
  const recommendations = generatedReview?.recommendations || [];
  const keyRisks = generatedReview?.key_risks || [];

  return (
    <div>
      <SaveBar onSave={() => onSave({ improvement_plan: draft })} saving={saving} dirty={dirty} label="Save Improvement Plan" />

      <div className="bg-ink text-white border border-ink/10 rounded-lg shadow-saas mb-6 p-5">
        <h2 className="font-bold tracking-tight text-xl mb-1">Improvement Plan</h2>
        <p className="font-medium text-white/80 text-sm">
          Built from your Results. The findings, risks and recommendations generated from your
          assessment drive the actions below — capture quick wins, strategic priorities, a 90-day
          plan and a 12-month roadmap.
        </p>
      </div>

      {!generatedReview ? (
        <div className="bg-white border border-ink/10 rounded-lg shadow-saas p-8 text-center mb-6">
          <AlertTriangle size={36} className="mx-auto mb-3" />
          <h2 className="font-bold tracking-tight text-2xl mb-2">Generate your results first</h2>
          <p className="font-medium text-ink/70 mb-6 max-w-md mx-auto">
            The improvement plan is based on your generated results. Complete the assessment, then
            generate the review on the Results tab to populate findings, risks and recommended actions
            here.
          </p>
          <button
            onClick={() => onGoTo && onGoTo('results')}
            className="inline-flex items-center px-6 py-3 bg-ink text-white border border-ink/10 rounded-lg shadow-saas font-bold uppercase tracking-wide"
          >
            Go to Results
            <ArrowRight size={16} className="ml-2" />
          </button>
        </div>
      ) : (
        <div className="bg-lime border border-ink/10 rounded-lg shadow-saas mb-6 p-4 flex items-center justify-between gap-3 flex-wrap">
          <span className="font-bold uppercase text-sm tracking-wide">
            Based on results · Overall maturity {generatedReview?.overall?.score ?? '—'} / 5
          </span>
          <button
            onClick={() => onGoTo && onGoTo('results')}
            className="inline-flex items-center px-4 py-2 bg-ink text-white border border-ink/10 rounded-lg shadow-saas-sm font-bold uppercase text-sm tracking-wide"
          >
            View full results
            <ArrowRight size={14} className="ml-2" />
          </button>
        </div>
      )}

      {keyRisks.length > 0 && (
        <SectionCard title="Key Risks to Address" icon={AlertTriangle} subtitle="Highest-priority gaps from your results">
          <div className="space-y-2">
            {keyRisks.map((risk, i) => (
              <div key={i} className="bg-cream border border-ink/10 rounded-lg p-3">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <AlertTriangle size={16} className="flex-shrink-0" />
                  <span className="font-bold">{risk.title}</span>
                  {risk.priority && (
                    <span className="text-xs font-bold uppercase px-2 py-0.5 border border-ink/10 rounded-lg bg-white flex-shrink-0">
                      {risk.priority}
                    </span>
                  )}
                </div>
                {risk.finding && <p className="text-sm font-medium text-ink/70">{risk.finding}</p>}
                {risk.pillar_name && (
                  <p className="text-xs font-bold uppercase tracking-wide text-ink/50 mt-1">{risk.pillar_name}</p>
                )}
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {recommendations.length > 0 && (
        <SectionCard title="Recommendations from Results" icon={ClipboardList} subtitle="Generated from your assessment — turn these into actions below">
          <div className="space-y-2">
            {recommendations.map((rec, i) => (
              <div key={i} className="bg-cream border border-ink/10 rounded-lg p-3 flex items-start gap-3">
                {rec.priority && (
                  <span className="text-xs font-bold uppercase px-2 py-0.5 border border-ink/10 rounded-lg bg-white flex-shrink-0">
                    {rec.priority}
                  </span>
                )}
                <div>
                  <span className="font-medium text-sm">{rec.text}</span>
                  {(rec.pillar_name || rec.reference_name) && (
                    <p className="text-xs font-bold uppercase tracking-wide text-ink/50 mt-1">
                      {rec.pillar_name}{rec.reference_name ? ` · ${rec.reference_name}` : ''}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {autoPlan.length > 0 && (
        <SectionCard title="Generated 90-Day Actions" icon={Target} subtitle="From the analysis engine — use as a starting point">
          <div className="space-y-2">
            {autoPlan.map((item, i) => (
              <div key={i} className="bg-cream border border-ink/10 rounded-lg p-3 flex items-start gap-3">
                {item.timeline && (
                  <span className="text-xs font-bold uppercase px-2 py-0.5 border border-ink/10 rounded-lg bg-white flex-shrink-0">
                    {item.timeline}
                  </span>
                )}
                <div>
                  <span className="font-medium text-sm">{item.action}</span>
                  {item.owner && (
                    <p className="text-xs font-bold uppercase tracking-wide text-ink/50 mt-1">{item.owner}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      <SectionCard title="Quick Wins" icon={Zap} subtitle="High-impact actions achievable in weeks">
        <StringListEditor
          items={draft.quick_wins}
          onChange={(v) => set('quick_wins', v)}
          placeholder="e.g. Adopt a standard board paper template"
          addLabel="Add Quick Win"
        />
      </SectionCard>

      <SectionCard title="Strategic Priorities" icon={TrendingUp} subtitle="Longer-term governance initiatives">
        <StringListEditor
          items={draft.strategic_priorities}
          onChange={(v) => set('strategic_priorities', v)}
          placeholder="e.g. Establish a formal board skills matrix and succession plan"
          addLabel="Add Strategic Priority"
        />
      </SectionCard>

      <SectionCard title="90-Day Action Plan" icon={Target} subtitle="Your own prioritised actions">
        <ObjectListEditor
          items={draft.action_plan_90day}
          onChange={(v) => set('action_plan_90day', v)}
          addLabel="Add Action"
          fields={[
            { key: 'action', label: 'Action', placeholder: 'What needs to happen', full: true, textarea: true },
            { key: 'owner', label: 'Owner', placeholder: 'e.g. Chair' },
            { key: 'timeline', label: 'Timeline', placeholder: 'e.g. 0–30 days' },
          ]}
        />
      </SectionCard>

      <SectionCard title="12-Month Roadmap" icon={Calendar} subtitle="Milestones across the year">
        <ObjectListEditor
          items={draft.roadmap_12month}
          onChange={(v) => set('roadmap_12month', v)}
          addLabel="Add Milestone"
          fields={[
            { key: 'quarter', label: 'Quarter / Period', placeholder: 'e.g. Q2' },
            { key: 'milestone', label: 'Milestone', placeholder: 'Target outcome' },
          ]}
        />
      </SectionCard>
    </div>
  );
};

export default ImprovementPlanTab;
